/* ============================================================================
 * ALTIORA — Diagnostic conversationnel IMES™ v2
 * ----------------------------------------------------------------------------
 * Refonte UX post-doctrine §6 (7 couches ontologiques) et Principe 6 (respect radical).
 * « Le logiciel doit disparaître. »
 *
 * Architecture :
 *   - 7 chapitres = les 7 couches ontologiques
 *   - À chaque chapitre : écran d'introduction + questions 1-par-écran + feedback IA
 *   - Écran d'accueil rituel + écran de clôture émotionnel avant l'analyse
 *   - Framer-motion pour transitions douces (respiration, pas d'animation gratuite)
 * ==========================================================================*/
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "@/lib/api";
import GlobalLocationSearch from "@/components/GlobalLocationSearch";
import { altioraToast } from "@/components/altiora/toast";
import { PageShell } from "@/components/altiora/PremiumUI";
import {
  ArrowRight, ArrowLeft, Loader2, Sparkles, CheckCircle2, Wind,
} from "lucide-react";

/* ============================================================================
 * CHAPITRES — les 7 couches ontologiques (Doctrine §6)
 * Chaque chapitre a :
 *   - dim (upstream|current|downstream) : dimension backend
 *   - kicker : sur-titre discret
 *   - opening_fr : introduction humaine (1-2 phrases)
 *   - questions[] : chacune {key, prompt_fr, hint_fr?, type, options?, unit?, min?, max?}
 *   - closing_fr : phrase de clôture avec feedback (fonction ou string)
 * ==========================================================================*/
const CHAPITRES = [
  // ── Chapitre 1 — Origine ────────────────────────────────────────────────
  {
    id: "origine",
    dim: "upstream",
    kicker: "Chapitre 1 · Origine",
    title_fr: "D'où vient votre histoire",
    opening_fr:
      "Personne ne choisit son point de départ. Mais votre point de départ nous aide à mesurer, avec justesse, la distance déjà parcourue et celle qui reste possible.",
    questions: [
      {
        key: "family_income_level",
        prompt_fr: "Quand vous étiez enfant, à quoi ressemblait la vie économique de votre famille ?",
        hint_fr: "Approximation, sans jugement.",
        type: "select",
        options: [
          ["low", "Nous avions du mal à joindre les deux bouts"],
          ["modest", "Simple mais suffisant"],
          ["middle", "Confortable, sans excès"],
          ["upper_middle", "Aisé"],
          ["high", "Privilégié"],
        ],
      },
      {
        key: "parents_education_max",
        prompt_fr: "Jusqu'où vos parents (ou tuteurs) ont-ils mené leurs études ?",
        type: "select",
        options: [
          ["none", "Pas d'école"],
          ["primary", "Primaire"],
          ["secondary", "Secondaire"],
          ["college", "Collégial / technique"],
          ["bachelor", "Baccalauréat"],
          ["graduate", "Cycles supérieurs"],
        ],
      },
      {
        key: "migration_status",
        prompt_fr: "Comment décririez-vous votre parcours migratoire ?",
        type: "select",
        options: [
          ["born_here", "Je suis né(e) ici"],
          ["settled_permanent", "Résident permanent, installé depuis longtemps"],
          ["recent_immigrant", "Immigrant récent (< 5 ans)"],
          ["temporary", "Statut temporaire"],
          ["refugee", "Réfugié ou demandeur d'asile"],
        ],
      },
      {
        key: "highest_education",
        prompt_fr: "Et vous, jusqu'où avez-vous mené vos études ?",
        type: "select",
        options: [
          ["none", "Aucune"],
          ["primary", "Primaire"],
          ["secondary", "Secondaire"],
          ["college", "Collégial"],
          ["bachelor", "Baccalauréat"],
          ["master", "Maîtrise"],
          ["doctorate", "Doctorat"],
        ],
      },
      {
        key: "family_financial_support_available",
        prompt_fr: "Aujourd'hui, si vous en aviez besoin, votre famille pourrait-elle vous soutenir financièrement ?",
        type: "select",
        options: [
          ["none", "Non, pas du tout"],
          ["occasional", "Occasionnellement"],
          ["regular", "Oui, régulièrement"],
          ["significant", "Oui, de manière significative"],
        ],
      },
    ],
    closing_fr: () => "Merci pour cette confiance. Continuons.",
  },

  // ── Chapitre 2 — Fondations ─────────────────────────────────────────────
  {
    id: "fondations",
    dim: "current",
    kicker: "Chapitre 2 · Fondations",
    title_fr: "Ce qui vous porte aujourd'hui",
    opening_fr:
      "Vos compétences, vos langues, vos années d'expérience — ce sont les racines solides sur lesquelles tout le reste peut pousser.",
    questions: [
      { key: "age", prompt_fr: "Quel âge avez-vous ?", type: "number", unit: "ans", min: 16, max: 100 },
      {
        key: "languages_spoken",
        prompt_fr: "Quelles langues parlez-vous à un niveau professionnel ?",
        hint_fr: "Ajoutez celles où vous êtes à l'aise en réunion, pas seulement lues.",
        type: "multi_text",
        placeholder: "français, anglais, arabe…",
      },
      {
        key: "tech_proficiency_self_rating",
        prompt_fr: "Comment évaluez-vous votre aisance avec les outils numériques ?",
        type: "rating",
      },
      {
        key: "financial_literacy_self_rating",
        prompt_fr: "Et avec les concepts financiers (budget, épargne, placements) ?",
        type: "rating",
      },
      {
        key: "certifications_count",
        prompt_fr: "Combien de certifications professionnelles avez-vous ?",
        type: "number",
        min: 0,
        max: 50,
      },
    ],
    closing_fr: () => "Vos racines sont ancrées. Continuons vers votre présent.",
  },

  // ── Chapitre 3 — Économie du foyer ──────────────────────────────────────
  {
    id: "economie",
    dim: "current",
    kicker: "Chapitre 3 · Économie",
    title_fr: "Comment vous vivez aujourd'hui",
    opening_fr:
      "Les chiffres ne définissent pas qui vous êtes. Ils dessinent la marge que vous avez pour agir. Répondez avec des ordres de grandeur.",
    questions: [
      {
        key: "employment_status",
        prompt_fr: "Aujourd'hui, votre situation professionnelle, c'est plutôt...",
        type: "select",
        options: [
          ["employed_full", "Un emploi à temps plein"],
          ["employed_part", "Un emploi à temps partiel"],
          ["self_employed", "Travailleur autonome"],
          ["unemployed", "Sans emploi"],
          ["retired", "Retraité(e)"],
          ["student", "Étudiant(e)"],
          ["disability", "En situation d'invalidité"],
        ],
      },
      {
        key: "sector",
        prompt_fr: "Dans quel secteur travaillez-vous (ou souhaitez travailler) ?",
        type: "text",
        placeholder: "tech, santé, éducation…",
      },
      {
        key: "city",
        prompt_fr: "Dans quelle ville vivez-vous ?",
        hint_fr: "Où que vous soyez dans le monde — un client en consultation peut être ici même.",
        type: "location",
        placeholder: "Casablanca, Montréal, Paris, Dakar…",
      },
      {
        key: "monthly_net_income_cad",
        prompt_fr: "Combien recevez-vous, environ, chaque mois — après impôts ?",
        hint_fr: "Un ordre de grandeur suffit.",
        type: "number",
        unit: "CAD / mois",
        min: 0,
      },
      {
        key: "monthly_essential_expenses_cad",
        prompt_fr: "Et combien partent chaque mois pour l'essentiel — loyer, nourriture, transports ?",
        type: "number",
        unit: "CAD / mois",
        min: 0,
      },
      {
        key: "number_of_income_streams",
        prompt_fr: "Combien de sources de revenus avez-vous en tout ?",
        hint_fr: "Emploi principal, freelance, loyers, placements…",
        type: "number",
        min: 0,
        max: 10,
      },
    ],
    closing_fr: (answers) => {
      const net = Number(answers.monthly_net_income_cad || 0);
      const exp = Number(answers.monthly_essential_expenses_cad || 0);
      if (net && exp && net > exp) {
        const ratio = ((net / exp) * 100 - 100).toFixed(0);
        return `Votre marge mensuelle est d'environ +${ratio}% au-dessus de vos essentiels. Une marge, c'est du carburant pour la trajectoire.`;
      }
      return "Nous notons ces ordres de grandeur. Continuons.";
    },
  },

  // ── Chapitre 4 — Patrimoine ─────────────────────────────────────────────
  {
    id: "patrimoine",
    dim: "current",
    kicker: "Chapitre 4 · Patrimoine",
    title_fr: "Ce que vous avez construit",
    opening_fr:
      "Ce chapitre parle de coussin. Un fonds d'urgence, une épargne, une propriété. Ce ne sont pas des mesures de valeur personnelle — juste de résilience.",
    questions: [
      {
        key: "total_assets_cad",
        prompt_fr: "Vos actifs — épargne, placements, propriété — représentent environ combien ?",
        type: "number",
        unit: "CAD",
        min: 0,
      },
      {
        key: "total_liabilities_cad",
        prompt_fr: "Et vos dettes — cartes, prêts, hypothèque — additionnées ?",
        type: "number",
        unit: "CAD",
        min: 0,
      },
      {
        key: "emergency_fund_months",
        prompt_fr: "Si vos revenus s'arrêtaient demain, combien de mois de dépenses essentielles pourriez-vous couvrir ?",
        type: "number",
        unit: "mois",
        min: 0,
        max: 60,
      },
      {
        key: "has_disability_insurance",
        prompt_fr: "Avez-vous une assurance invalidité ?",
        hint_fr: "Elle protège votre revenu si vous ne pouviez plus travailler.",
        type: "bool",
      },
      {
        key: "has_life_insurance",
        prompt_fr: "Une assurance vie ?",
        type: "bool",
      },
    ],
    closing_fr: (answers) => {
      const ef = Number(answers.emergency_fund_months || 0);
      if (ef >= 6) return "Votre coussin de sécurité est solide. C'est rare — c'est précieux.";
      if (ef >= 3) return "Vous avez un coussin honorable. Nous verrons comment le prolonger.";
      if (ef >= 1) return "Vous avez commencé à construire votre coussin. Le prochain palier est à portée.";
      return "Bâtir un premier mois de fonds d'urgence sera notre première recommandation.";
    },
  },

  // ── Chapitre 5 — Capital humain & réseau ────────────────────────────────
  {
    id: "capital_humain",
    dim: "current",
    kicker: "Chapitre 5 · Réseau",
    title_fr: "Les personnes autour de vous",
    opening_fr:
      "Personne ne progresse seul. Le réseau, ce n'est pas des cartes de visite — c'est du temps partagé, des conseils reçus, des mains tendues.",
    questions: [
      {
        key: "strong_professional_relationships",
        prompt_fr: "Combien de personnes de confiance pourriez-vous appeler pour un vrai conseil professionnel ?",
        hint_fr: "Pas des contacts LinkedIn — des relations solides.",
        type: "number",
        min: 0,
        max: 500,
      },
      {
        key: "mentor_relationship_current",
        prompt_fr: "Avez-vous en ce moment un mentor — quelqu'un qui vous fait grandir régulièrement ?",
        type: "bool",
      },
      {
        key: "formal_leadership_experience",
        prompt_fr: "Avez-vous déjà encadré ou dirigé une équipe formellement ?",
        type: "bool",
      },
      {
        key: "community_involvement_self_rating",
        prompt_fr: "À quel point êtes-vous impliqué(e) dans une communauté (professionnelle, associative, culturelle) ?",
        type: "rating",
      },
    ],
    closing_fr: (answers) => {
      if (answers.mentor_relationship_current) return "Vous avez un mentor. C'est un multiplicateur silencieux.";
      return "Un mentor peut multiplier votre vitesse par 2 ou 3. Nous vous aiderons à en trouver.";
    },
  },

  // ── Chapitre 6 — Bien-être ──────────────────────────────────────────────
  {
    id: "bien_etre",
    dim: "current",
    kicker: "Chapitre 6 · Bien-être",
    title_fr: "Comment vous allez, vraiment",
    opening_fr:
      "La finance sans la santé, c'est un château sur le sable. Prenez un instant. Répondez à voix basse, comme à un proche.",
    questions: [
      {
        key: "financial_stress_self_rating",
        prompt_fr: "En ce moment, sur une échelle de 1 (serein) à 10 (accablé), où est votre stress lié à l'argent ?",
        type: "rating",
      },
      {
        key: "life_satisfaction_self_rating",
        prompt_fr: "Et votre satisfaction générale de vie ?",
        type: "rating",
      },
      {
        key: "self_perceived_health",
        prompt_fr: "Comment décririez-vous votre santé perçue ?",
        type: "select",
        options: [
          ["excellent", "Excellente"],
          ["good", "Bonne"],
          ["fair", "Correcte"],
          ["poor", "Fragile"],
        ],
      },
    ],
    closing_fr: (answers) => {
      const stress = Number(answers.financial_stress_self_rating || 0);
      if (stress >= 7) return "Nous entendons. La trajectoire commence par soulager la pression, pas par optimiser.";
      if (stress >= 4) return "Un stress modéré est un signal utile — il indique où concentrer l'attention.";
      return "Vous êtes dans une position calme pour construire. Précieux.";
    },
  },

  // ── Chapitre 7 — Aspirations ────────────────────────────────────────────
  {
    id: "aspirations",
    dim: "downstream",
    kicker: "Chapitre 7 · Aspirations",
    title_fr: "Où vous voulez aller",
    opening_fr:
      "Un diagnostic sans direction, c'est une carte sans destination. Aidez-nous à comprendre ce vers quoi vous marchez.",
    questions: [
      {
        key: "primary_goal_fr",
        prompt_fr: "En une phrase : quel est votre objectif principal pour les 5 prochaines années ?",
        type: "textarea",
        placeholder: "Racheter du temps · lancer mon projet · retrouver la sérénité financière…",
      },
      {
        key: "target_income_5y_cad",
        prompt_fr: "Quel revenu annuel visez-vous dans 5 ans ?",
        hint_fr: "Ambitieux mais crédible.",
        type: "number",
        unit: "CAD / an",
        min: 0,
      },
      {
        key: "weekly_hours_available_for_growth",
        prompt_fr: "Combien d'heures par semaine pouvez-vous consacrer à votre progression ?",
        hint_fr: "Formation, réseautage, projets parallèles.",
        type: "number",
        unit: "h / sem",
        min: 0,
        max: 60,
      },
      {
        key: "monthly_investment_capacity_cad",
        prompt_fr: "Combien pourriez-vous investir chaque mois dans vous-même — formation, outils, conseils ?",
        type: "number",
        unit: "CAD / mois",
        min: 0,
      },
      {
        key: "risk_tolerance",
        prompt_fr: "Face à un risque financier, vous vous décrivez plutôt comme…",
        type: "select",
        options: [
          ["conservative", "Prudent — je préfère la stabilité"],
          ["moderate", "Modéré — j'accepte une part calculée"],
          ["growth", "Orienté croissance — je prends des risques éclairés"],
          ["aggressive", "Audacieux — je vise haut"],
        ],
      },
      {
        key: "considering_business_launch",
        prompt_fr: "Envisagez-vous de lancer ou développer une entreprise ?",
        type: "bool",
      },
      {
        key: "considering_career_change",
        prompt_fr: "Un changement de carrière ?",
        type: "bool",
      },
      {
        key: "considering_further_education",
        prompt_fr: "Une formation additionnelle ou un retour aux études ?",
        type: "bool",
      },
      {
        key: "open_to_relocation",
        prompt_fr: "Êtes-vous ouvert(e) à déménager pour une bonne opportunité ?",
        type: "bool",
      },
      {
        key: "biggest_obstacle_perceived_fr",
        prompt_fr: "Enfin — qu'est-ce qui vous freine le plus, aujourd'hui, pour avancer ?",
        hint_fr: "Vous pouvez répondre librement.",
        type: "textarea",
      },
    ],
    closing_fr: () => "Votre direction est plus claire. Un dernier souffle avant la synthèse.",
  },
];

/* ============================================================================
 * TRANSITIONS — animations Framer Motion
 * ==========================================================================*/
const fadeSlide = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, y: -12, transition: { duration: 0.28 } },
};
const softFade = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.7 } },
  exit: { opacity: 0, transition: { duration: 0.25 } },
};

/* ============================================================================
 * COMPOSANTS de champ
 * ==========================================================================*/
function FieldSelect({ q, value, onChange }) {
  return (
    <div className="grid gap-3">
      {q.options.map(([val, label]) => (
        <button
          key={val}
          data-testid={`opt-${q.key}-${val}`}
          onClick={() => onChange(val)}
          className={`text-left px-6 py-5 rounded-xl border transition-all ${
            value === val
              ? "border-navy bg-navy text-white shadow-sm"
              : "border-[#E4E1D8] bg-white text-[#1F2937] hover:border-navy hover:-translate-y-0.5"
          }`}
        >
          <span className="text-lg md:text-xl font-medium">{label}</span>
        </button>
      ))}
    </div>
  );
}

function FieldBool({ q, value, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      {[["oui", true], ["non", false]].map(([lbl, val]) => (
        <button
          key={lbl}
          data-testid={`opt-${q.key}-${lbl}`}
          onClick={() => onChange(val)}
          className={`py-6 rounded-xl border transition-all text-xl font-medium ${
            value === val
              ? "border-navy bg-navy text-white"
              : "border-[#E4E1D8] bg-white text-[#1F2937] hover:border-navy hover:-translate-y-0.5"
          }`}
        >
          {lbl === "oui" ? "Oui" : "Non"}
        </button>
      ))}
    </div>
  );
}

function FieldNumber({ q, value, onChange }) {
  return (
    <div className="relative">
      <input
        type="number"
        inputMode="decimal"
        min={q.min}
        max={q.max}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        data-testid={`input-${q.key}`}
        className="w-full text-3xl md:text-4xl font-display text-navy bg-transparent border-0 border-b-2 border-[#E4E1D8] focus:border-navy outline-none py-4 placeholder:text-[#B8B5AC]"
        placeholder="—"
        autoFocus
      />
      {q.unit && <span className="absolute right-2 bottom-4 text-sm font-mono uppercase tracking-widest text-[#8B8B8F]">{q.unit}</span>}
    </div>
  );
}

function FieldText({ q, value, onChange, multiline = false }) {
  const Tag = multiline ? "textarea" : "input";
  return (
    <Tag
      type={multiline ? undefined : "text"}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={`input-${q.key}`}
      placeholder={q.placeholder || ""}
      rows={multiline ? 4 : undefined}
      className={`w-full text-xl md:text-2xl text-navy bg-transparent border-0 border-b-2 border-[#E4E1D8] focus:border-navy outline-none py-4 placeholder:text-[#B8B5AC] ${
        multiline ? "resize-none border rounded-xl px-4 py-3 border-[#E4E1D8]" : ""
      }`}
      autoFocus
    />
  );
}

function FieldMultiText({ q, value, onChange }) {
  // value stored as array; UI as comma-separated
  const text = Array.isArray(value) ? value.join(", ") : (value || "");
  return (
    <input
      type="text"
      value={text}
      onChange={(e) => {
        const items = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
        onChange(items);
      }}
      data-testid={`input-${q.key}`}
      placeholder={q.placeholder || ""}
      className="w-full text-xl md:text-2xl text-navy bg-transparent border-0 border-b-2 border-[#E4E1D8] focus:border-navy outline-none py-4 placeholder:text-[#B8B5AC]"
      autoFocus
    />
  );
}

function FieldRating({ q, value, onChange }) {
  return (
    <div>
      <div className="grid grid-cols-10 gap-2">
        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            data-testid={`rating-${q.key}-${n}`}
            onClick={() => onChange(n)}
            className={`aspect-square rounded-lg border font-medium text-lg transition-all ${
              value === n
                ? "border-navy bg-navy text-white shadow-sm"
                : "border-[#E4E1D8] bg-white text-[#5B5B62] hover:border-navy"
            }`}
          >
            {n}
          </button>
        ))}
      </div>
      <div className="flex justify-between mt-2 text-xs text-[#8B8B8F] font-mono uppercase tracking-widest">
        <span>Faible · 1</span>
        <span>10 · Élevé</span>
      </div>
    </div>
  );
}

function FieldLocation({ q, value, onChange }) {
  // value is stored as the human-readable "City, Country" label (backend compat = plain string)
  return (
    <div>
      <GlobalLocationSearch
        lang="fr"
        autoFocus
        placeholder={q.placeholder}
        onSelect={({ city, country, label }) => {
          onChange(city ? `${city}${country ? ", " + country : ""}` : label);
        }}
      />
      {value && (
        <div className="mt-3 inline-flex items-center gap-2 rounded-md bg-[#F1EFE9] border border-[#E4E1D8] px-3 py-1.5 text-sm text-navy">
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14]">Retenu</span>
          <span className="font-medium">{value}</span>
        </div>
      )}
    </div>
  );
}

function QuestionRenderer({ q, value, onChange }) {
  switch (q.type) {
    case "select": return <FieldSelect q={q} value={value} onChange={onChange} />;
    case "bool": return <FieldBool q={q} value={value} onChange={onChange} />;
    case "number": return <FieldNumber q={q} value={value} onChange={onChange} />;
    case "text": return <FieldText q={q} value={value} onChange={onChange} />;
    case "textarea": return <FieldText q={q} value={value} onChange={onChange} multiline />;
    case "multi_text": return <FieldMultiText q={q} value={value} onChange={onChange} />;
    case "rating": return <FieldRating q={q} value={value} onChange={onChange} />;
    case "location": return <FieldLocation q={q} value={value} onChange={onChange} />;
    default: return null;
  }
}

/* ============================================================================
 * PAGE PRINCIPALE
 * ==========================================================================*/
export default function Diagnostic() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [diag, setDiag] = useState(null);

  // Position dans le flow : "welcome" | { chapIdx, qIdx } | { chapIdx, mode: "opening"|"closing" } | "final"
  const [pos, setPos] = useState("welcome");
  const [savingSilently, setSavingSilently] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/diagnostic/me");
        setDiag(data.diagnostic);
        if (data.diagnostic.status === "completed") navigate("/app/diagnostic/report");
      } catch (err) {
        altioraToast.error("Impossible de charger le diagnostic.");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const answersByChap = useMemo(() => {
    if (!diag) return {};
    return {
      upstream: diag.upstream || {},
      current: diag.current || {},
      downstream: diag.downstream || {},
    };
  }, [diag]);

  const getAnswer = (dim, key) => answersByChap[dim]?.[key];
  const setAnswer = (dim, key, value) => {
    setDiag((d) => ({ ...d, [dim]: { ...(d[dim] || {}), [key]: value } }));
  };

  const persistChapter = async (chapIdx) => {
    const chap = CHAPITRES[chapIdx];
    setSavingSilently(true);
    try {
      const payload = { [chap.dim]: diag[chap.dim] || {} };
      await api.patch("/diagnostic/me", payload);
    } catch (err) {
      // silent failure — user can retry via nav
    } finally {
      setSavingSilently(false);
    }
  };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      // Save all sections
      for (let i = 0; i < CHAPITRES.length; i++) {
        await persistChapter(i);
      }
      altioraToast.info("Calcul IMES™ en cours…", { duration: 4000 });
      // Fast synchronous call — returns in <1s with scores + scenarios + analysis_status:"running"
      const { data } = await api.post("/diagnostic/me/analyze", null, { timeout: 15000 });
      // The IMES score is already computed. We can navigate to the report and let it show
      // partial state; the AI narrative fills in via polling.
      altioraToast.success("Votre trajectoire est prête. L'analyse IA se poursuit en arrière-plan.");
      // Kick off a lightweight background poll (non-blocking navigation)
      pollAnalysisStatus(data?.diagnostic_id);
      navigate("/app/diagnostic/report");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        altioraToast.error(err?.response?.data?.detail || "Trop de tentatives — réessayez plus tard.");
      } else {
        altioraToast.error(err?.response?.data?.detail || "L'analyse n'a pas pu être lancée. Vérifiez la section Économie (emploi ou revenu).");
      }
    } finally {
      setAnalyzing(false);
    }
  };

  // Silent poller — updates nothing here, but pings the endpoint so
  // subsequent report reload has fresh ai_analysis (called by the report page anyway)
  const pollAnalysisStatus = async (diagnosticId) => {
    if (!diagnosticId) return;
    const maxAttempts = 60; // ~3 minutes at 3s interval
    for (let i = 0; i < maxAttempts; i++) {
      try {
        const { data } = await api.get("/diagnostic/me/analyze/status");
        if (data.analysis_status === "completed" || data.analysis_status === "failed") {
          return; // report page will re-fetch on its own
        }
      } catch { /* keep polling */ }
      await new Promise((r) => setTimeout(r, 3000));
    }
  };

  if (loading) {
    return (
      <PageShell>
        <div className="max-w-3xl mx-auto px-6 py-14"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>
      </PageShell>
    );
  }

  // Progress computation
  const totalSteps = CHAPITRES.reduce((s, c) => s + c.questions.length + 2, 2); // +opening/closing per chap, welcome+final
  let currentStep = 1;
  if (pos !== "welcome") {
    if (pos === "final") currentStep = totalSteps - 1;
    else {
      currentStep = 2;
      for (let i = 0; i < pos.chapIdx; i++) currentStep += CHAPITRES[i].questions.length + 2;
      if (pos.mode === "opening") currentStep += 0;
      else if (pos.mode === "closing") currentStep += CHAPITRES[pos.chapIdx].questions.length + 1;
      else currentStep += 1 + pos.qIdx;
    }
  }
  const progress = Math.min(100, Math.round((currentStep / totalSteps) * 100));

  return (
    <PageShell testid="diagnostic-conversational">
      {/* Progress bar — discrète, en haut */}
      <div className="fixed top-0 left-0 right-0 z-40 h-[3px] bg-transparent" style={{ pointerEvents: "none" }}>
        <div
          className="h-full bg-navy transition-all duration-700 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="max-w-3xl mx-auto px-6 md:px-8 py-16 md:py-24 min-h-[calc(100vh-4rem)] flex flex-col justify-center">
        <AnimatePresence mode="wait">
          {/* ─── Écran d'accueil rituel ─────────────────────────────────── */}
          {pos === "welcome" && (
            <motion.div key="welcome" {...softFade} data-testid="diag-welcome" className="text-center">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1, transition: { delay: 0.2, duration: 0.9 } }}
                className="inline-flex items-center gap-2 mb-8"
              >
                <Wind className="h-5 w-5 text-[#7A5B14]" />
                <span className="text-xs font-mono uppercase tracking-[0.32em] text-[#7A5B14]">
                  Diagnostic ALTIORA · IMES™
                </span>
              </motion.div>
              <h1 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-tight text-navy leading-[1.05] mb-8">
                Prenez un souffle.<br />
                <span className="text-[#7A5B14]">Nous allons vous écouter.</span>
              </h1>
              <p className="text-lg md:text-xl text-[#374151] leading-relaxed max-w-xl mx-auto mb-12">
                Ce n&apos;est pas un formulaire. C&apos;est une conversation. À la fin, nous vous rendrons une lecture précise
                de votre trajectoire économico-sociale — d&apos;où vous venez, où vous êtes, où vous pouvez aller.
              </p>
              <div className="text-sm text-[#5B5B62] mb-10">
                <div>7 chapitres · ~ 12 minutes · réponses sauvegardées automatiquement</div>
              </div>
              <button
                data-testid="diag-start-btn"
                onClick={() => setPos({ chapIdx: 0, mode: "opening" })}
                className="inline-flex items-center gap-3 px-8 py-4 bg-navy text-white rounded-full text-lg font-medium hover:bg-[#123163] transition-all hover:-translate-y-0.5"
              >
                Commencer <ArrowRight className="h-5 w-5" />
              </button>
            </motion.div>
          )}

          {/* ─── Écrans de chapitre : opening / question / closing ────── */}
          {typeof pos === "object" && pos.chapIdx !== undefined && (() => {
            const chap = CHAPITRES[pos.chapIdx];
            const answers = answersByChap[chap.dim] || {};
            const key = pos.mode === "opening" ? `open-${chap.id}`
                      : pos.mode === "closing" ? `close-${chap.id}`
                      : `q-${chap.id}-${pos.qIdx}`;

            if (pos.mode === "opening") {
              return (
                <motion.div key={key} {...fadeSlide} data-testid={`diag-opening-${chap.id}`}>
                  <div className="text-xs font-mono uppercase tracking-[0.32em] text-[#7A5B14] mb-6">
                    {chap.kicker}
                  </div>
                  <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-navy leading-[1.1] mb-8">
                    {chap.title_fr}
                  </h2>
                  <p className="text-lg md:text-xl text-[#374151] leading-relaxed mb-14 max-w-2xl">
                    {chap.opening_fr}
                  </p>
                  <button
                    data-testid={`diag-open-next-${chap.id}`}
                    onClick={() => setPos({ chapIdx: pos.chapIdx, qIdx: 0 })}
                    className="inline-flex items-center gap-3 px-7 py-3.5 bg-navy text-white rounded-full text-base font-medium hover:bg-[#123163] transition-all"
                  >
                    Continuer <ArrowRight className="h-4 w-4" />
                  </button>
                </motion.div>
              );
            }

            if (pos.mode === "closing") {
              const closingText = typeof chap.closing_fr === "function" ? chap.closing_fr(answers) : chap.closing_fr;
              return (
                <motion.div key={key} {...fadeSlide} data-testid={`diag-closing-${chap.id}`} className="text-center">
                  <motion.div
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1, transition: { delay: 0.15, type: "spring", stiffness: 130 } }}
                    className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 mb-8"
                  >
                    <CheckCircle2 className="h-8 w-8" />
                  </motion.div>
                  <p className="font-display text-2xl md:text-3xl text-navy leading-snug max-w-xl mx-auto mb-12">
                    {closingText}
                  </p>
                  <div className="flex items-center justify-center gap-4">
                    <button
                      onClick={() => setPos({ chapIdx: pos.chapIdx, qIdx: chap.questions.length - 1 })}
                      className="text-sm text-[#5B5B62] hover:text-navy underline underline-offset-4"
                    >
                      Revenir
                    </button>
                    <button
                      data-testid={`diag-next-chap-${chap.id}`}
                      onClick={async () => {
                        await persistChapter(pos.chapIdx);
                        if (pos.chapIdx + 1 < CHAPITRES.length) {
                          setPos({ chapIdx: pos.chapIdx + 1, mode: "opening" });
                        } else {
                          setPos("final");
                        }
                      }}
                      className="inline-flex items-center gap-3 px-7 py-3.5 bg-navy text-white rounded-full text-base font-medium hover:bg-[#123163] transition-all"
                    >
                      Chapitre suivant <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </motion.div>
              );
            }

            // Question mode
            const q = chap.questions[pos.qIdx];
            const value = getAnswer(chap.dim, q.key);
            const isLast = pos.qIdx === chap.questions.length - 1;
            const isFirst = pos.qIdx === 0;

            return (
              <motion.div key={key} {...fadeSlide} data-testid={`diag-question-${chap.id}-${pos.qIdx}`}>
                <div className="text-xs font-mono uppercase tracking-[0.32em] text-[#7A5B14] mb-4">
                  {chap.kicker} · {pos.qIdx + 1}/{chap.questions.length}
                </div>
                <h2 className="font-display text-2xl md:text-3xl lg:text-4xl text-navy leading-[1.2] mb-4">
                  {q.prompt_fr}
                </h2>
                {q.hint_fr && (
                  <p className="text-sm md:text-base text-[#5B5B62] mb-8 max-w-xl">{q.hint_fr}</p>
                )}
                <div className="mb-12 max-w-xl">
                  <QuestionRenderer
                    q={q}
                    value={value}
                    onChange={(v) => setAnswer(chap.dim, q.key, v)}
                  />
                </div>

                <div className="flex items-center justify-between gap-4 max-w-xl">
                  <button
                    onClick={() => {
                      if (isFirst) setPos({ chapIdx: pos.chapIdx, mode: "opening" });
                      else setPos({ chapIdx: pos.chapIdx, qIdx: pos.qIdx - 1 });
                    }}
                    className="inline-flex items-center gap-2 text-sm text-[#5B5B62] hover:text-navy"
                  >
                    <ArrowLeft className="h-4 w-4" /> Retour
                  </button>
                  <button
                    data-testid={`diag-next-q-${chap.id}-${pos.qIdx}`}
                    onClick={async () => {
                      if (isLast) {
                        await persistChapter(pos.chapIdx);
                        setPos({ chapIdx: pos.chapIdx, mode: "closing" });
                      } else {
                        setPos({ chapIdx: pos.chapIdx, qIdx: pos.qIdx + 1 });
                      }
                    }}
                    className="inline-flex items-center gap-3 px-6 py-3 bg-navy text-white rounded-full text-base font-medium hover:bg-[#123163] transition-all"
                  >
                    {isLast ? "Terminer ce chapitre" : "Suivant"} <ArrowRight className="h-4 w-4" />
                  </button>
                </div>

                {savingSilently && (
                  <div className="mt-8 text-xs text-[#8B8B8F] font-mono">Sauvegarde…</div>
                )}
              </motion.div>
            );
          })()}

          {/* ─── Écran final émotionnel avant l'analyse ─────────────────── */}
          {pos === "final" && (
            <motion.div key="final" {...softFade} data-testid="diag-final" className="text-center">
              <motion.div
                initial={{ scale: 0.85, opacity: 0 }}
                animate={{ scale: 1, opacity: 1, transition: { delay: 0.15, duration: 0.9 } }}
                className="mb-10"
              >
                <Sparkles className="h-8 w-8 text-[#C5A880] mx-auto" />
              </motion.div>
              <h2 className="font-display text-3xl md:text-5xl text-navy leading-[1.1] mb-8">
                Vous avez tout dit.<br />
                <span className="text-[#7A5B14]">Écoutons maintenant votre trajectoire.</span>
              </h2>
              <p className="text-lg text-[#374151] max-w-xl mx-auto mb-12 leading-relaxed">
                L&apos;analyse combine vos réponses avec notre méthode IMES™ v2 et l&apos;intelligence de Claude Sonnet.
                Elle prend environ 60 à 90 secondes.
              </p>
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={() => setPos({ chapIdx: CHAPITRES.length - 1, mode: "closing" })}
                  className="text-sm text-[#5B5B62] hover:text-navy underline underline-offset-4"
                >
                  Revenir en arrière
                </button>
                <button
                  data-testid="diag-analyze-btn"
                  onClick={analyze}
                  disabled={analyzing}
                  className="inline-flex items-center gap-3 px-8 py-4 bg-navy text-white rounded-full text-lg font-medium hover:bg-[#123163] transition-all hover:-translate-y-0.5 disabled:opacity-70"
                >
                  {analyzing ? <><Loader2 className="h-5 w-5 animate-spin" /> Analyse en cours…</> : <>Révéler ma trajectoire <ArrowRight className="h-5 w-5" /></>}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </PageShell>
  );
}
